'use client';

import { Select as BaseInput, SelectContent, SelectItem, SelectSeparator, SelectTrigger, SelectValue } from '@shared/components/shadcn';
import { forwardRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { FieldInfo } from '../FieldInfo/FieldInfo';
import { FieldMessage } from '../FieldMessage/FieldMessage';
import type { SelectOption } from '../types';

// Define option types
type SelectOptionGroup = {
  label: string;
  options: SelectOption[];
};

// Define component props
type SelectProps = {
  label?: string;
  id: string;
  name?: string; // Add name prop for form integration
  options: (SelectOption | SelectOptionGroup)[];
  disabled?: boolean;
  selectedValue?: string | number;
  value?: string | number; // Add value prop for react-hook-form
  onChange?: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  className?: string;
  'data-testid'?: string;
  'data-track-name'?: string;
  'data-track-privacy'?: string;
  'data-track-helper'?: string;
  'data-track-value'?: string;
  helpMessage?: string;
  helpMessageType?: 'error' | 'success' | 'info';
};

/**
 * Select component that supports both flat options and option groups
 */
const Select = forwardRef<HTMLButtonElement, SelectProps>(function Select(
  {
    label,
    id,
    name,
    options,
    disabled = false,
    selectedValue,
    value,
    onChange,
    onBlur,
    placeholder = 'Select an option',
    className = '',
    'data-testid': dataTestId,
    'data-track-name': dataTrackName,
    'data-track-privacy': dataTrackPrivacy,
    'data-track-helper': dataTrackHelper,
    'data-track-value': dataTrackValue,
    helpMessage,
    helpMessageType,
  },
  ref
) {
  const formContext = useFormContext();
  const fieldName = name || id;
  const contextHelpMessage = formContext?.formState?.errors?.[fieldName]?.message as string | undefined;
  const displayHelpMessage = helpMessage ?? contextHelpMessage;
  const displayHelpMessageType = helpMessageType ?? (contextHelpMessage ? 'error' : undefined);
  const hasError = displayHelpMessageType === 'error';

  // Use value from props or selectedValue
  const currentValue = value !== undefined ? value : selectedValue;

  // Helper function to check if an option is a group
  const isOptionGroup = (option: SelectOption | SelectOptionGroup): option is SelectOptionGroup => {
    return 'options' in option && Array.isArray((option as SelectOptionGroup).options);
  };

  // Check if a value is selected
  const hasValue = currentValue !== undefined && currentValue !== '';

  // Define classes using the conditional approach with dark mode support
  const classes = {
    wrapper: 'relative',
    trigger: `w-full data-[size=default]:h-12.5 py-2 px-4 shadow-none border-border !paragraph
              focus-visible:border-border-active focus-visible:outline-none focus-visible:ring-0
              data-[state=open]:border-border-active
              [&_svg]:size-4 data-[state=open]:[&_svg]:rotate-180 [&_svg]:opacity-100 [&_svg]:transition-transform
              ${hasError ? 'border-error-foreground focus-visible:border-error-foreground' : ''}
              ${className}`,
    value: `flex-1 truncate text-foreground`,
    dropdown: '!z-[9999] max-h-[180px] overflow-y-auto bg-container-base shadow-md border-none rounded-lg [&>svg]:!text-white',
    item: `my-0.5 px-3 py-2 text-foreground !paragraph rounded-md
           hover:bg-container focus-visible:bg-container
           data-[state=checked]:bg-container data-[state=checked]:!paragraph-strong
           disabled:opacity-50`,
    groupLabel: 'px-4 py-3 paragraph-strong',
    separator: 'dark:bg-border',
  };

  // Render options or option groups
  const renderOptions = () => {
    let hasRenderedFirstGroup = false;

    return options.map((option, index) => {
      // If it's an option group
      if (isOptionGroup(option)) {
        const groupContent = (
          <div key={`group-${index}`}>
            {hasRenderedFirstGroup && <SelectSeparator className={classes.separator} />}
            <div className={classes.groupLabel}>{option.label}</div>
            {option.options.map(groupOption => (
              <SelectItem
                key={`${index}-${groupOption.value}`}
                value={String(groupOption.value)}
                disabled={groupOption.disabled}
                className={classes.item}
                data-track-value={groupOption.dataTrackValue}
                data-testid={`select-item-${groupOption.value}`}
              >
                {groupOption.text}
              </SelectItem>
            ))}
          </div>
        );

        hasRenderedFirstGroup = true;
        return groupContent;
      }

      // If it's a regular option
      return (
        <SelectItem
          key={`option-${index}`}
          value={String(option.value)}
          disabled={option.disabled}
          className={classes.item}
          data-track-value={option.dataTrackValue}
          data-testid={`select-item-${option.value}`}
        >
          {option.text}
        </SelectItem>
      );
    });
  };

  return (
    <>
      <div className={classes.wrapper}>
        {label && <FieldInfo label={label} htmlFor={id} />}
        <BaseInput data-testid={dataTestId} disabled={disabled} value={currentValue !== undefined ? String(currentValue) : undefined} onValueChange={onChange}>
          <SelectTrigger
            ref={ref}
            id={id}
            data-has-value={hasValue ? 'true' : 'false'}
            className={classes.trigger}
            data-track-name={dataTrackName}
            data-track-privacy={dataTrackPrivacy}
            data-track-value={dataTrackValue}
            onBlur={onBlur}
          >
            <SelectValue placeholder={placeholder} className={classes.value} />
          </SelectTrigger>
          <SelectContent
            className={classes.dropdown}
            position="popper"
            sideOffset={4}
            avoidCollisions={true}
            style={{ minWidth: 'var(--radix-select-trigger-width)', width: 'var(--radix-select-trigger-width)' }}
          >
            <div className="p-2">{renderOptions()}</div>
          </SelectContent>
        </BaseInput>
        <FieldMessage message={displayHelpMessage} messageType={displayHelpMessageType} disabled={disabled} data-track-helper={dataTrackHelper} />
      </div>
    </>
  );
});

export { Select, type SelectOption };
